import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RocketLaunch, CurrencyDollar, Lightning, Sparkle } from '@phosphor-icons/react';

const FreeLimitModal = ({ 
  isOpen, 
  onClose, 
  promptData,
  onBuildOffer 
}) => {
  const navigate = useNavigate();

  if (!isOpen || !promptData) return null;

  const handleAction = (action) => {
    switch (action) {
      case 'open_offer_builder':
        onClose();
        if (onBuildOffer) {
          onBuildOffer();
        } else {
          window.dispatchEvent(new CustomEvent('openModal', { detail: 'buildOffer' }));
        }
        break;
      case 'open_pricing':
        onClose();
        navigate('/pricing');
        break;
      case 'open_checkout':
        onClose();
        navigate('/pricing');
        break;
      default:
        void('Unknown action:', action);
    }
  };

  const getButtonIcon = (actionId) => {
    switch (actionId) {
      case 'build_offer':
        return <RocketLaunch size={20} weight="fill" />;
      case 'view_pricing':
        return <CurrencyDollar size={20} weight="fill" />;
      case 'upgrade':
        return <Lightning size={20} weight="fill" />;
      default:
        return <Sparkle size={20} weight="fill" />;
    }
  };

  const getButtonStyle = (variant) => {
    switch (variant) {
      case 'primary':
        return 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white';
      case 'secondary':
        return 'bg-white/10 hover:bg-white/20 text-white';
      default:
        return 'bg-white/10 hover:bg-white/20 text-white';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="paygbot-modal-shell bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="free-limit-modal"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition-colors z-10"
          >
            <X size={20} />
          </button>

          {/* Header with Gradient */}
          <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkle size={32} className="text-white" weight="fill" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {promptData.title}
            </h2>
            <p className="text-lg text-blue-200">
              {promptData.message}
            </p>
            {promptData.subtitle && (
              <p className="text-slate-400 mt-2 text-sm">
                {promptData.subtitle}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 space-y-3">
            {promptData.actions?.map((action) => (
              <button
                key={action.id}
                onClick={() => handleAction(action.action)}
                data-testid={`limit-action-${action.id}`}
                className={`w-full py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center space-x-3 ${getButtonStyle(action.variant)}`}
              >
                {getButtonIcon(action.id)}
                <span>{action.label}</span>
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 text-center">
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
            >
              Maybe later
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FreeLimitModal;
